import { useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, IconButton, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useQuery } from "@tanstack/react-query";
import {
  packagesApi, type PackageDto, type PackageLineRequest, type QuotedPackageDto,
} from "@/api/endpoints";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/** One package on the bill, with what the counter says was actually delivered. */
export interface SelectedPackage {
  key: string;
  pkg: PackageDto;
  /** Delivered quantity per component id. Starts at what the package includes. */
  delivered: Record<string, number>;
}

export const toPackageRequests = (selected: SelectedPackage[]): PackageLineRequest[] =>
  selected.map(s => ({
    packageId: s.pkg.id,
    components: s.pkg.components.map(c => ({
      componentId: c.id,
      qty: s.delivered[c.id] ?? c.qty,
    })),
  }));

/**
 * Selling a package at the counter (W18).
 *
 * <p>A package is added as one thing and bills as many: the server expands it into ordinary lines
 * with the fixed price spread across them. The counter never sees or sets those rates — what it
 * sets is <b>what was actually delivered</b>, one number per component, and the package's own
 * policies decide what that does to the price.</p>
 *
 * <p>The delivered column is the whole interaction. Leaving it alone bills the package as defined,
 * which is the common case; turning a quantity down is a skip and turning it up is an extra, and
 * the quote comes back saying what either did to the total.</p>
 */
export function PackagesPanel({
  selected, onChange, quoted, insuranceSchemeId, documentDate, disabled,
}: {
  selected: SelectedPackage[];
  onChange: (next: SelectedPackage[]) => void;
  /** What the server said these expand to. Empty until the first quote lands. */
  quoted: QuotedPackageDto[];
  insuranceSchemeId: string;
  documentDate: string;
  disabled?: boolean;
}) {
  const [picking, setPicking] = useState(false);

  // Only what can actually be sold on the bill's date — a package out of its window would be
  // refused on create, and offering it at the counter wastes the patient's time.
  const available = useQuery({
    queryKey: ["packages", "sellable", documentDate, insuranceSchemeId],
    queryFn: () => packagesApi.list({ availableOn: documentDate, isActive: true, pageSize: 200 }),
    staleTime: 60_000,
  });

  // A payer-specific package on a self-pay bill is refused server-side, so it is not offered here.
  const options = (available.data?.items ?? []).filter(
    p => !p.insuranceSchemeId || p.insuranceSchemeId === insuranceSchemeId);

  const add = (pkg: PackageDto) => {
    onChange([...selected, {
      key: crypto.randomUUID(),
      pkg,
      delivered: Object.fromEntries(pkg.components.map(c => [c.id, c.qty])),
    }]);
    setPicking(false);
  };

  const setDelivered = (key: string, componentId: string, qty: number) =>
    onChange(selected.map(s => s.key === key
      ? { ...s, delivered: { ...s.delivered, [componentId]: qty } }
      : s));

  if (selected.length === 0 && !picking) {
    return (
      <Button size="small" startIcon={<AddIcon />} disabled={disabled}
        onClick={() => setPicking(true)}>
        Add a package
      </Button>
    );
  }

  return (
    <Stack spacing={2}>
      {selected.map(s => {
        const q = quoted.find(x => x.packageId === s.pkg.id);
        return (
          <Paper key={s.key} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                  <Typography variant="subtitle2">{s.pkg.name}</Typography>
                  <Chip size="small" variant="outlined" label={s.pkg.code} />
                  {q && q.price !== q.listPrice && (
                    <Tooltip title="Part of the package was not delivered, and this package credits pro-rata">
                      <Chip size="small" color="warning" variant="outlined"
                        label={`credited to ${inr.format(q.price)}`} />
                    </Tooltip>
                  )}
                </Stack>
                {q && (
                  <Typography variant="caption" color="text.secondary">
                    Components worth {inr.format(q.componentValue)} separately
                    {q.saving > 0 ? ` — a saving of ${inr.format(q.saving)}` : ""}
                  </Typography>
                )}
              </Box>
              <Stack alignItems="flex-end">
                <Typography variant="h6">{inr.format(q?.price ?? s.pkg.price)}</Typography>
                <Typography variant="caption" color="text.secondary">fixed price</Typography>
              </Stack>
              <IconButton size="small" disabled={disabled}
                onClick={() => onChange(selected.filter(x => x.key !== s.key))}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Includes</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Included</TableCell>
                  <TableCell align="right" sx={{ width: 130 }}>Delivered</TableCell>
                  <TableCell sx={{ width: 200 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {s.pkg.components.map(c => {
                  const delivered = s.delivered[c.id] ?? c.qty;
                  const skipped = delivered < c.qty;
                  const extra = delivered > c.qty;
                  return (
                    <TableRow key={c.id}>
                      <TableCell>
                        {c.serviceName ?? c.itemName ?? c.sku ?? "—"}
                        <Typography component="span" variant="caption" color="text.secondary"
                          sx={{ ml: 1 }}>
                          {c.lineType}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{c.qty}</TableCell>
                      <TableCell align="right">
                        <TextField type="number" size="small" sx={{ width: 100 }}
                          value={delivered} disabled={disabled}
                          onChange={(e) =>
                            setDelivered(s.key, c.id, Math.max(0, Number(e.target.value) || 0))}
                          inputProps={{ min: 0, step: "1", style: { textAlign: "right" } }} />
                      </TableCell>
                      <TableCell>
                        {skipped && (
                          <Typography variant="caption" color="warning.main">
                            {s.pkg.skippedPolicy === "ProRataCredit"
                              ? "credited pro-rata"
                              : "price stands"}
                          </Typography>
                        )}
                        {extra && (
                          <Typography variant="caption" color="info.main">
                            {s.pkg.extraPolicy === "IncludedNoCharge"
                              ? "covered by the package"
                              : "billed separately"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {q && q.notes.length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {q.notes.map((n, i) => <Box key={i}>{n}</Box>)}
              </Alert>
            )}
          </Paper>
        );
      })}

      {picking ? (
        <Autocomplete
          size="small" autoFocus openOnFocus
          options={options}
          getOptionLabel={(o) => `${o.code} — ${o.name} (${inr.format(o.price)})`}
          loading={available.isLoading}
          onChange={(_, v) => { if (v) add(v); }}
          onBlur={() => setPicking(false)}
          renderInput={(p) => (
            <TextField {...p} label="Package" placeholder="Search packages on offer today…"
              helperText={options.length === 0 && !available.isLoading
                ? "Nothing is on offer for this patient on this date."
                : undefined} />
          )}
        />
      ) : (
        <Button size="small" startIcon={<AddIcon />} disabled={disabled}
          sx={{ alignSelf: "flex-start" }} onClick={() => setPicking(true)}>
          Add another package
        </Button>
      )}
    </Stack>
  );
}
